import { useState } from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import { trans } from '@spotahome/soyuz/client';

import { useInitRecaptcha } from '../../../context/RecaptchaContext';
import FormProvider from '../../FormProvider';

import Success from './Success';

import styles from './ContactForm.module.scss';

const formSchema = yup.object().shape({
  email: yup
    .string()
    .ensure()
    .required('common.form.required-field')
    .email('lyp.validation.email.format'),
  name: yup.string().ensure().required('common.form.required-field'),
  comment: yup.string().ensure().required('common.form.required-field'),
  phoneNumber: yup.string().ensure().required('common.form.required-field')
});

let values = null;

const ContactForm = ({
  error = null,
  initialValues,
  onSubmit,
  onToggleAction,
  clearError
}) => {
  const [isFormSubmitted, setFormSubmitted] = useState(null);
  const [recaptchaPassed, setRecaptchaPassed] = useState(false);
  const [loading, setLoading] = useState(false);
  const { renderRecaptchaComponent, isRecaptchaReady } = useInitRecaptcha({
    action: 'contact-form',
    onChange: () => setRecaptchaPassed(true),
    onExpired: () => setRecaptchaPassed(false),
    className: styles['contact-form__recaptcha']
  });

  const handleFormSubmit = async payload => {
    setLoading(true);
    try {
      await onSubmit(payload);
      values = payload;
      setFormSubmitted(true);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  const hasError = isFormSubmitted && !!error;

  return !isFormSubmitted || hasError ? (
    <FormProvider
      onSubmit={handleFormSubmit}
      schema={formSchema}
      initialValues={initialValues}
      fieldsClassName={styles['contact-form__field']}
      disabled={!recaptchaPassed}
      isLoading={loading}
    >
      <FormProvider.Input
        type="text"
        name="name"
        title={trans('contact-us.field.name.title')}
        placeholder={trans('contact-us.field.name.placeholder')}
        required
        onBlurHandler={clearError}
      />

      <FormProvider.Input
        type="email"
        name="email"
        title={trans('contact-us.field.email.title')}
        placeholder={trans('contact-us.field.email.placeholder')}
        required
        onBlurHandler={clearError}
      />

      <FormProvider.PhoneInput
        id="phoneNumber"
        name="phoneNumber"
        title={trans('contact-us.field.phoneNumber.title')}
        required
        onBlurHandler={clearError}
      />

      <FormProvider.Input
        type="textarea"
        name="comment"
        title={trans('contact-us.field.comment.title')}
        placeholder={trans('contact-us.field.comment.placeholder')}
        required
        rows={4}
        onBlurHandler={clearError}
      />

      {renderRecaptchaComponent()}

      <div className={styles['contact-form__footer']}>
        <span className={styles['contact-form__submit']}>
          <FormProvider.Submit disabled={!isRecaptchaReady}>
            {trans('loginsignup.modal.continue')}
          </FormProvider.Submit>
        </span>
        {hasError && (
          <p
            data-test="contact-form-error"
            className={styles['contact-form__error']}
          >
            {error}
          </p>
        )}
      </div>
    </FormProvider>
  ) : (
    <Success {...values} onToggleAction={onToggleAction} />
  );
};

ContactForm.propTypes = {
  error: PropTypes.shape({
    handler: PropTypes.string,
    message: PropTypes.string
  }),
  initialValues: PropTypes.shape({
    email: PropTypes.string,
    password: PropTypes.string
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onToggleAction: PropTypes.func.isRequired,
  clearError: PropTypes.func.isRequired
};

export default ContactForm;
